import React from 'react';
import { Collapse, Typography } from 'antd';
import { PlusOutlined, TeamOutlined, SearchOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { ColumnsType } from 'antd/es/table';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  SearchInput,
  TeamManagementButton,
  TeamManagementLinkButton,
  TeamManagementIconWrapper,
  TeamManagementActionsContainer,
} from '../Styles';
import Table from '../../../commonComponents/Table';
import { useTheme } from '../../../context/ThemeContext';
import type { MerchantOrManager } from '../../../redux/Settings';
import type { TeamManagementSectionProps } from '../types';

const { Panel } = Collapse;

const TeamManagementSection: React.FC<TeamManagementSectionProps> = ({
  role,
  showMerchantsAndManagers,
  filteredMerchantsAndManagers,
  loading,
  searchValue,
  onToggle,
  onSearch,
  onMoreDetailsClick,
}) => {
  const { isDarkMode } = useTheme();

  const columns: ColumnsType<MerchantOrManager> = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <TeamManagementLinkButton type="link" onClick={onMoreDetailsClick}>
          {text}
        </TeamManagementLinkButton>
      ),
    },
    {
      title: 'USERNAME',
      dataIndex: 'username',
      key: 'username',
      render: (text: string) => text || '-',
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      render: (text: string) => text || '-',
    },
    {
      title: 'PHONE',
      dataIndex: 'phone',
      key: 'phone',
      render: (text: string) => text || '-',
    },
    {
      title: 'ADDRESS',
      dataIndex: 'address',
      key: 'address',
      render: (text: string) => text || '-',
    },
  ];

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <TeamManagementIconWrapper>
            <FontAwesomeIcon icon={faUsers} />
          </TeamManagementIconWrapper>
          <CardTitleText level={3}>
            {role === 'admin' ? 'Merchant Management' : 'Manager Management'}
          </CardTitleText>
        </CardTitle>
        <TeamManagementButton icon={<PlusOutlined />} onClick={onToggle}>
          {showMerchantsAndManagers ? 'Hide' : 'View'} {role === 'admin' ? 'Merchants' : 'Managers'}
        </TeamManagementButton>
      </CardHeader>

      <Collapse activeKey={showMerchantsAndManagers ? ['1'] : []} ghost>
        <Panel key="1" header={null} showArrow={false}>
          <TeamManagementActionsContainer>
            <Typography.Text type="secondary">
              {role === 'admin' ? 'Manage your merchant accounts' : 'Manage your store managers'}
            </Typography.Text>
            <TeamManagementButton icon={<TeamOutlined />} onClick={onMoreDetailsClick}>
              {role === 'admin' ? 'Advanced Settings' : 'Manager Settings'}
            </TeamManagementButton>
          </TeamManagementActionsContainer>

          <SearchInput
            placeholder={`Search ${role === 'admin' ? 'merchants' : 'managers'}...`}
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={onSearch}
            allowClear
          />

          <Table<MerchantOrManager>
            columns={columns}
            dataSource={filteredMerchantsAndManagers}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={filteredMerchantsAndManagers.length > 0 ? { x: true, y: 600 } : undefined}
            emptyText={`No ${role === 'admin' ? 'merchants' : 'managers'} found`}
            headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
            removeVerticalBorders={true}
            thPadding="15px 10px"
            tdPadding="10px 10px"
            firstChildBorderRadius="8px"
            border="0"
            borderBottomLeftRight={true}
            theme={isDarkMode ? 'dark' : 'light'}
          />
        </Panel>
      </Collapse>
    </SettingsCard>
  );
};

export default React.memo(TeamManagementSection);

